import React from 'react';
import { Formik, Form, getIn } from 'formik';
import type { FormikProps } from 'formik';
import { Box, ButtonBase, TextField } from '@mui/material';
import { NumericFormat } from 'react-number-format';
import type { PLRFormProps } from '../../../../../../types/preloan';
import { FormSchemaV3 } from '../../../../../../types/preloan/v3';
import { patchFillForm, postPreApprove } from '../../../../../../queries/preloan/v3';
import { getInterestRate } from '../../../../../../queries/preloan';
import { formInputs as contactInputs, handleInputSetter } from '../../../../ContactForm/inputsData';
import {
  archiveUnemployedPLR,
  partnerErrorRedirectURL, preLoanRequestStep,
} from '../../formLogic';
import analytics, { addPreApproveId } from '../../../../../../utils/analytics';
import { inviteePlrCategory } from '../../../ComplementInvite/analyticsPages';
import { fieldsFromPreApprove } from '../02_dataFetching/components/helpers';
import PlrShell from '../../../landing/PlrShell';
import PlrOptions, { PlrOption } from '../../../landing/PlrOptions';
import PlrModal from '../../../landing/PlrModal';
import { briefcaseIcon, checkCircleIcon, mailIcon } from '../../../landing/icons';
import {
  PlrButton, PlrFieldLabel, PlrLead, PlrNotice, PlrTextLink, PlrTitle, ProjectLabel, plrInputSx,
} from '../../../landing/ui';
import { partnerFromSchema, useShowHero } from '../../../landing/partner';
import { projectEntityLabel } from '../../../heroVariant';
import { plr } from '../../../landing/tokens';
import fillFormValidation from './validation';

const EXTERNAL_INFO_ERROR = 'No pudimos procesar tu evaluación porque uno de nuestros proveedores de información está temporalmente fuera de servicio. Vuelve a intentarlo más tarde.';
const GENERIC_ERROR = 'Ocurrió un error. Inténtalo de nuevo o contáctanos.';

const CONTACT_LABELS: Record<string, string> = {
  email: 'Tu correo',
  phone: 'Tu celular (WhatsApp)',
};

type DataField = {
  label: string,
  kind: 'number' | 'money' | 'options',
  suffix?: string,
  placeholder?: string,
  options?: PlrOption<string>[],
};

// Mismo orden que DataForm/inputsData.
const DATA_FIELDS: Record<string, DataField> = {
  age: { label: 'Tu edad', kind: 'number', suffix: 'años' },
  sex: {
    label: 'Género',
    kind: 'options',
    options: [{ value: 'M', title: 'Masculino' }, { value: 'F', title: 'Femenino' }],
  },
  type_of_contract: {
    label: 'Tipo de contrato',
    kind: 'options',
    options: [
      { value: 'INDEFINIDO', title: 'Indefinido' },
      { value: 'DEFINIDO', title: 'Plazo fijo' },
      { value: 'DESEMPLEADO', title: 'Sin contrato' },
    ],
  },
  brute_salary: { label: 'Sueldo bruto mensual', kind: 'money', placeholder: 'Ej: 1.200.000' },
  net_salary: { label: 'Sueldo líquido mensual', kind: 'money', placeholder: 'Ej: 950.000' },
  emp_length: { label: 'Meses en tu empleo actual', kind: 'number', suffix: 'meses' },
  licenses: { label: 'Días de licencia médica en los últimos 2 meses', kind: 'number', suffix: 'días' },
};
const DATA_ORDER = Object.keys(DATA_FIELDS);

// Un dato de contacto viene "precargado" cuando trae algo más que el valor por defecto.
const isPrefilled = (key: string, value: string) => value.trim() !== ''
  && value.trim() !== (contactInputs[key]?.defaultValue || '').trim();

function fieldError(form: FormikProps<FormSchemaV3>, name: string) {
  return getIn(form.touched, name) ? (getIn(form.errors, name) as string | undefined) : undefined;
}

function ContactField({ form, fieldKey, original }: {
  form: FormikProps<FormSchemaV3>,
  fieldKey: string,
  original: FormSchemaV3['contactForm'][string],
}) {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const field = form.values.contactForm[fieldKey];
  const name = `contactForm.${fieldKey}.value`;
  const error = fieldError(form, name);
  const kind = field.type === 'Email' ? 'mail' : 'teléfono';
  const canUndo = isPrefilled(fieldKey, original.value);

  const handleChange = () => {
    analytics.track('Modificar Datos', { initiator: 'usuario', text: 'Quiero modificar este campo', dataModified: kind });
    // Valor y "ok" en una sola llamada: con dos, la validación intermedia ve el estado a medias.
    form.setFieldTouched(name, false, false);
    form.setFieldValue(`contactForm.${fieldKey}`, {
      ...field, value: contactInputs[fieldKey]?.defaultValue || '', ok: false,
    });
    setTimeout(() => inputRef.current?.focus(), 0);
  };
  const handleUndo = () => {
    analytics.track('Modificar Datos', { initiator: 'usuario', text: 'Revertir cambios', dataModified: kind });
    // Vuelve al dato que teníamos y oculta el error del campo editado.
    form.setFieldTouched(name, false, false);
    form.setFieldValue(`contactForm.${fieldKey}`, { ...field, value: original.value, ok: true });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <PlrFieldLabel
        htmlFor={`fill-form-${fieldKey}`}
        aside={!field.ok && canUndo ? (
          <PlrTextLink onClick={handleUndo} testId={`fill-form-${fieldKey}-undo`} sx={{ fontSize: 13.5 }}>
            Deshacer
          </PlrTextLink>
        ) : undefined}
      >
        {CONTACT_LABELS[fieldKey] || contactInputs[fieldKey]?.title || fieldKey}
      </PlrFieldLabel>
      {field.ok ? (
        <Box
          data-pw={`fill-form-${fieldKey}`}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: '12px',
            height: { xs: 54, sm: 56 },
            boxSizing: 'border-box',
            px: '16px',
            borderRadius: '16px',
            background: plr.surface,
            fontSize: { xs: 16, sm: 17 },
            color: plr.ink,
          }}
        >
          <Box
            component="span"
            id={`fill-form-${fieldKey}`}
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
          >
            {field.value}
          </Box>
          <PlrTextLink
            onClick={handleChange}
            testId={`fill-form-${fieldKey}-change`}
            sx={{ fontSize: 14, alignSelf: 'center' }}
          >
            Cambiar
          </PlrTextLink>
        </Box>
      ) : (
        <TextField
          id={`fill-form-${fieldKey}`}
          name={name}
          inputRef={inputRef}
          value={field.value}
          onChange={(event) => handleInputSetter(
            field.type,
            form.setFieldValue,
            name,
            event.target.value,
          )}
          onBlur={form.handleBlur}
          placeholder={contactInputs[fieldKey]?.placeholder}
          type={field.type === 'Email' ? 'email' : 'tel'}
          autoComplete={field.type === 'Email' ? 'email' : 'tel'}
          error={!!error}
          helperText={error}
          inputProps={{ 'data-pw': `fill-form-${fieldKey}-input` }}
          sx={plrInputSx}
        />
      )}
      {field.ok && error && (
        <Box component="span" role="alert" sx={{ mx: '4px', fontSize: 12.5, color: '#C0392B' }}>
          {error}
        </Box>
      )}
    </Box>
  );
}

function Adornment({ side, children }: { side: 'start' | 'end', children: React.ReactNode }) {
  return (
    <Box
      component="span"
      sx={{ color: plr.muted, [side === 'start' ? 'mr' : 'ml']: '6px', flexShrink: 0 }}
    >
      {children}
    </Box>
  );
}

function DataFieldInput({ form, fieldKey }: { form: FormikProps<FormSchemaV3>, fieldKey: string }) {
  const config: DataField = DATA_FIELDS[fieldKey] || { label: fieldKey, kind: 'number' };
  const name = `dataForm.${fieldKey}.value`;
  const value = form.values.dataForm[fieldKey]?.value ?? '';
  const error = fieldError(form, name);
  const labelId = `fill-form-${fieldKey}-label`;

  if (config.kind === 'options' && config.options) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        <Box id={labelId} sx={{ fontSize: { xs: 14, sm: 15 }, fontWeight: 700, color: plr.ink }}>
          {config.label}
        </Box>
        <PlrOptions
          layout="pills"
          options={config.options}
          value={value}
          onChange={(option) => {
            form.setFieldValue(name, option);
            form.setFieldTouched(name, true, false);
          }}
          labelledBy={labelId}
          testIdPrefix={`fill-form-${fieldKey}`}
          error={!!error}
        />
        {error && (
          <Box component="span" role="alert" sx={{ mx: '4px', fontSize: 12.5, color: '#C0392B' }}>
            {error}
          </Box>
        )}
      </Box>
    );
  }

  const money = config.kind === 'money';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      <PlrFieldLabel htmlFor={`fill-form-${fieldKey}`}>{config.label}</PlrFieldLabel>
      <NumericFormat
        id={`fill-form-${fieldKey}`}
        name={name}
        value={value}
        onBlur={form.handleBlur}
        onValueChange={({ value: raw }: { value: string }) => form.setFieldValue(name, raw)}
        customInput={TextField}
        thousandSeparator={money ? '.' : false}
        decimalSeparator=","
        decimalScale={0}
        allowNegative={false}
        autoComplete="off"
        placeholder={config.placeholder}
        error={!!error}
        helperText={error}
        InputProps={{
          startAdornment: money ? <Adornment side="start">$</Adornment> : undefined,
          endAdornment: config.suffix ? <Adornment side="end">{config.suffix}</Adornment> : undefined,
          inputProps: { 'data-pw': `fill-form-${fieldKey}-input`, inputMode: 'numeric' },
        }}
        sx={plrInputSx}
      />
    </Box>
  );
}

type Section = 'contact' | 'data';

const formatDataValue = (key: string, value: string) => {
  const config = DATA_FIELDS[key];
  if (!value) return '';
  if (!config) return value;
  if (config.kind === 'money') return `$${Number(value).toLocaleString('es-CL')}`;
  if (config.kind === 'options') return config.options?.find((o) => o.value === value)?.title || value;
  return config.suffix ? `${value} ${config.suffix}` : value;
};

function SectionRow({
  section, title, summary, confirmed, onOpen,
}: {
  section: Section,
  title: string,
  summary: string,
  confirmed: boolean,
  onOpen: () => void,
}) {
  return (
    <ButtonBase
      type="button"
      data-pw={`fill-form-${section}-row`}
      data-status={confirmed ? 'confirmed' : 'pending'}
      onClick={onOpen}
      sx={{
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        gap: { xs: '10px', sm: '12px' },
        p: { xs: '12px', sm: '14px 14px 14px 16px' },
        borderRadius: '16px',
        background: '#FFFFFF',
        boxShadow: `inset 0 0 0 1px ${plr.border}`,
        textAlign: 'left',
        fontFamily: plr.font,
        '&:hover': { boxShadow: `inset 0 0 0 1px ${plr.faint}` },
        '&.Mui-focusVisible': { outline: `3px solid ${plr.stepCurrent}`, outlineOffset: '2px' },
      }}
    >
      <Box
        component="span"
        aria-hidden="true"
        sx={{
          flexShrink: 0,
          width: { xs: 36, sm: 40 },
          height: { xs: 36, sm: 40 },
          borderRadius: '12px',
          background: plr.surface,
          color: confirmed ? '#1E7B4F' : plr.accent,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {/* eslint-disable-next-line no-nested-ternary */}
        {confirmed ? checkCircleIcon({ size: 20, strokeWidth: 2.2 })
          : (section === 'contact' ? mailIcon({ size: 19 }) : briefcaseIcon({ size: 19 }))}
      </Box>
      <Box
        component="span"
        sx={{
          flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: '2px',
        }}
      >
        <Box component="span" sx={{ fontSize: { xs: 15, sm: 16 }, fontWeight: 700, color: plr.ink }}>
          {title}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: 13,
            lineHeight: 1.4,
            color: confirmed ? plr.muted : plr.accent,
            fontWeight: confirmed ? 400 : 600,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {confirmed ? summary : 'Pendiente'}
        </Box>
      </Box>
      <Box
        component="span"
        sx={{
          flexShrink: 0,
          height: 38,
          px: '14px',
          display: 'flex',
          alignItems: 'center',
          borderRadius: '20px',
          fontSize: 14,
          fontWeight: 700,
          color: confirmed ? plr.ink : '#FFFFFF',
          background: confirmed ? '#FFFFFF' : plr.ink,
          boxShadow: confirmed ? `inset 0 0 0 1.5px ${plr.ink}` : 'none',
        }}
      >
        {confirmed ? 'Editar' : 'Completar'}
      </Box>
    </ButtonBase>
  );
}

// Contenido de "Confirma tus datos": una fila por sección y cada sección se completa en una
// hoja (PlrModal), como el flujo anterior (diálogos de contacto y de datos complementarios).
function FillFormBody({
  form, contactKeys, dataKeys, originalContact, complementToken, error,
}: {
  form: FormikProps<FormSchemaV3>,
  contactKeys: string[],
  dataKeys: string[],
  originalContact: FormSchemaV3['contactForm'],
  complementToken?: string | null,
  error: React.ReactNode,
}) {
  const hasContact = contactKeys.length > 0;
  const hasData = dataKeys.length > 0;
  const [open, setOpen] = React.useState<Section | null>(null);
  const [confirmed, setConfirmed] = React.useState<Record<Section, boolean>>({
    contact: !hasContact, data: !hasData,
  });
  const submitting = React.useRef(false);
  const category = inviteePlrCategory(complementToken, 'PLR - CONFIRMAR DATOS');

  const submit = async () => {
    if (submitting.current) return;
    submitting.current = true;
    try {
      await form.submitForm();
    } finally {
      submitting.current = false;
    }
  };

  const openSection = (section: Section) => {
    analytics.page(category, section === 'contact' ? 'DATOS DE CONTACTO' : 'DATOS COMPLEMENTARIOS');
    setConfirmed((prev) => ({ ...prev, [section]: false }));
    setOpen(section);
  };

  React.useEffect(() => {
    const first: Section | null = (hasContact && 'contact') || (hasData && 'data') || null;
    if (!first) return undefined;
    const id = setTimeout(() => openSection(first), 500);
    return () => clearTimeout(id);
  }, []);

  const confirmSection = async (section: Section) => {
    const keys = section === 'contact'
      ? contactKeys.map((k) => `contactForm.${k}.value`)
      : dataKeys.map((k) => `dataForm.${k}.value`);
    keys.forEach((name) => form.setFieldTouched(name, true, false));
    const errors = await form.validateForm();
    const sectionErrors = section === 'contact' ? errors.contactForm : errors.dataForm;
    if (sectionErrors && Object.keys(sectionErrors).length > 0) return;
    const next = { ...confirmed, [section]: true };
    setConfirmed(next);
    setOpen(null);
    const other: Section = section === 'contact' ? 'data' : 'contact';
    if (!next[other]) {
      setTimeout(() => openSection(other), 500);
      return;
    }
    // Como el flujo anterior: al confirmar lo último que faltaba se envía solo.
    // "Continuar" queda para reintentar si el envío falla.
    submit();
  };

  const contactSummary = contactKeys
    .map((k) => form.values.contactForm[k]?.value).filter(Boolean).join(' · ');
  const dataSummary = dataKeys
    .map((k) => formatDataValue(k, form.values.dataForm[k]?.value)).filter(Boolean).join(' · ');
  const masked = contactKeys.some((k) => originalContact[k]?.value.includes('**'));
  const allConfirmed = confirmed.contact && confirmed.data;

  const modalLead = (text: string) => (
    <Box
      component="p"
      sx={{
        m: '-6px 0 2px', fontSize: { xs: 14, sm: 15 }, lineHeight: 1.45, color: plr.body,
      }}
    >
      {text}
    </Box>
  );

  return (
    <Box sx={{
      display: 'flex', flexDirection: 'column', flex: 1, gap: '10px',
    }}
    >
      {hasContact && (
        <SectionRow
          section="contact"
          title="Datos de contacto"
          summary={contactSummary}
          confirmed={confirmed.contact}
          onOpen={() => openSection('contact')}
        />
      )}
      {hasData && (
        <SectionRow
          section="data"
          title="Tu trabajo e ingresos"
          summary={dataSummary}
          confirmed={confirmed.data}
          onOpen={() => openSection('data')}
        />
      )}
      {error && <Box sx={{ mt: '6px' }}>{error}</Box>}
      <Box sx={{
        mt: 'auto', pt: '14px', display: 'flex', flexDirection: 'column',
      }}
      >
        <PlrButton
          testId="preloan-fill-form-submit"
          onClick={submit}
          disabled={!allConfirmed}
          loading={form.isSubmitting}
        >
          Continuar
        </PlrButton>
      </Box>

      <PlrModal
        open={open === 'contact'}
        onClose={() => setOpen(null)}
        title="Confirma tus datos de contacto"
        titleId="fill-form-contact-title"
        testId="contact-form-dialog-title"
        width={480}
      >
        {modalLead(masked
          ? 'Los sacamos de tu información de contacto. Si alguno no es tuyo o cambió, actualízalo.'
          : 'Nos faltan estos datos para seguir con tu evaluación.')}
        {contactKeys.map((key) => (
          <ContactField
            key={key}
            form={form}
            fieldKey={key}
            original={originalContact[key]}
          />
        ))}
        <PlrButton
          testId="fill-form-contact-confirm"
          onClick={() => confirmSection('contact')}
          sx={{ mt: '6px', width: '100%' }}
        >
          Confirmar
        </PlrButton>
      </PlrModal>

      <PlrModal
        open={open === 'data'}
        onClose={() => setOpen(null)}
        title="Cuéntanos de tu trabajo"
        titleId="fill-form-data-title"
        testId="data-form-dialog"
        width={480}
      >
        {modalLead('Con esto terminamos de calcular tu evaluación.')}
        {dataKeys.map((key) => <DataFieldInput key={key} form={form} fieldKey={key} />)}
        <PlrButton
          testId="fill-form-data-confirm"
          onClick={() => confirmSection('data')}
          sx={{ mt: '6px', width: '100%' }}
        >
          Confirmar
        </PlrButton>
      </PlrModal>
    </Box>
  );
}

FillFormBody.defaultProps = { complementToken: null };

export default function FillForm<T extends FormSchemaV3>(props: PLRFormProps<T>) {
  const {
    formRef,
    formSchema,
    handleCurrentData,
    handleStep,
  } = props;
  const partner = partnerFromSchema(formSchema);
  const showHero = useShowHero(formSchema);
  const [error, setError] = React.useState(false);
  const [errorExternalInformation, setErrorExternalInformation] = React.useState(false);

  const contactKeys = Object.keys(formSchema.contactForm);
  const dataKeys = Object.keys(formSchema.dataForm)
    .sort((a, b) => (DATA_ORDER.indexOf(a) + 1 || 99) - (DATA_ORDER.indexOf(b) + 1 || 99));

  // Los datos de contacto vacíos se piden directo (antes había que marcar "Quiero modificar").
  const initialValues = React.useMemo(() => {
    const contactForm = { ...formSchema.contactForm };
    contactKeys.forEach((key) => {
      const field = contactForm[key];
      contactForm[key] = { ...field, ok: field.ok && isPrefilled(key, field.value) };
    });
    return { ...formSchema, contactForm } as T;
  }, []);

  React.useEffect(() => {
    if (error) {
      const redirectURL = partnerErrorRedirectURL(
        formSchema.origin,
        formSchema.rut,
        formSchema.config,
      );
      if (redirectURL) {
        setTimeout(() => { window.location.href = redirectURL; }, 1000);
      }
    }
  }, [error]);

  React.useEffect(() => {
    const category = inviteePlrCategory(
      formSchema.complementToken,
      formSchema.isRedirect ? `PLR - ${formSchema.isRedirect}` : 'PLR - CONFIRMAR DATOS',
    );
    analytics.page(category, 'CONFIRMAR DATOS');
    handleCurrentData({ ...formSchema, isRedirect: false });
  }, []);

  return (
    <Formik
      innerRef={formRef}
      initialValues={initialValues}
      validationSchema={fillFormValidation(contactKeys, dataKeys)}
      onSubmit={async (values) => {
        setError(false);
        setErrorExternalInformation(false);
        try {
          const parsedQueryData = {} as { [key: string]: string | number };
          Object.keys(values.contactForm).forEach((key) => {
            parsedQueryData[key] = values.contactForm[key]?.value.trim();
          });
          Object.keys(values.dataForm).forEach((key) => {
            parsedQueryData[key] = values.dataForm[key]?.type === 'number'
              ? +(values.dataForm[key].value.trim())
              : values.dataForm[key]?.value.trim();
          });
          const { data } = await patchFillForm(values.PLRtoken, parsedQueryData);
          if (values.dataForm.type_of_contract?.value === 'DESEMPLEADO') {
            return archiveUnemployedPLR(formSchema, handleCurrentData, handleStep);
          }

          switch (data.plr.step) {
            case 'DATA_OK': {
              const { data: preApproveData } = await postPreApprove(formSchema.PLRtoken);
              if (preApproveData.preApproves?.length > 0) {
                const mainPreApprove = preApproveData.preApproves.find((pa) => pa.productSubtype === 'main');
                const selectedPreApprove = mainPreApprove || preApproveData.preApproves[0];
                addPreApproveId(selectedPreApprove.id);
              }
              switch (preApproveData.plr.step) {
                case 'LOAN_SIMULATION': {
                  const interestRate = await getInterestRate(formSchema.PLRtoken);
                  handleCurrentData({
                    ...formSchema,
                    step: preApproveData.plr.step,
                    loanParams: interestRate.data,
                  });
                  return handleStep(preLoanRequestStep.LOAN_SIMULATION);
                }
                case 'MANUAL_VERIFICATION':
                case 'READY_FOR_LOAN': {
                  handleCurrentData({
                    ...formSchema,
                    step: preApproveData.plr.step,
                    preApproves: preApproveData.preApproves,
                    ...fieldsFromPreApprove(preApproveData),
                  });
                  return handleStep(preLoanRequestStep.READY_FOR_LOAN);
                }
                case 'ARCHIVED_FAIL': {
                  handleCurrentData({
                    ...formSchema,
                    step: preApproveData.plr.step,
                    firstName: preApproveData.plr.firstName,
                    lastName: preApproveData.plr.lastName,
                    rut: preApproveData.plr.idNumber,
                    results: preApproveData.results,
                    ...fieldsFromPreApprove(preApproveData),
                    origin: preApproveData.plr.origin,
                    adminComment: preApproveData.plr.adminComment,
                  });
                  return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
                }
                default:
                  return setError(true);
              }
            }
            case 'ARCHIVED_FAIL': {
              handleCurrentData({
                ...formSchema,
                step: data.plr.step,
                firstName: data.plr.firstName,
                lastName: data.plr.lastName,
                rut: data.plr.idNumber,
                results: data.results,
                origin: data.plr.origin,
                adminComment: data.plr.adminComment,
              });
              return handleStep(preLoanRequestStep.ARCHIVED_FAIL);
            }
            case 'MANUAL_VERIFICATION':
            case 'READY_FOR_LOAN': {
              handleCurrentData({
                ...formSchema,
                step: data.plr.step,
              });
              return handleStep(preLoanRequestStep.READY_FOR_LOAN);
            }
            default:
              return setError(true);
          }
        } catch (e) {
          if (e === 509) {
            analytics.track('ERROR DE FUENTE INFORMACION EXTERNA', {
              initiator: 'backend',
              errorCode: e,
            });
            setErrorExternalInformation(true);
          } else {
            analytics.track('ERROR', {
              initiator: 'backend',
              errorCode: e,
            });
          }
          return setError(true);
        }
      }}
    >
      {(form) => (
        <Form style={{ width: '100%' }} noValidate>
          <PlrShell
            partner={partner}
            showHero={showHero}
            progress={{ step: 2, label: 'Tus datos' }}
            testId="preloan-fill-form-step"
          >
            {showHero && (
              <ProjectLabel hideOnPanel>
                {projectEntityLabel(formSchema.partnerEntityName, formSchema.partner)}
              </ProjectLabel>
            )}
            <PlrTitle size="step" sx={{ mt: showHero ? '-6px' : 0 }}>
              Confirma tus datos
            </PlrTitle>
            <PlrLead>Nos faltan algunos datos para terminar tu evaluación.</PlrLead>
            <FillFormBody
              // FormikProps<T> no es asignable a FormikProps<FormSchemaV3> (invariante en T).
              form={form as unknown as FormikProps<FormSchemaV3>}
              contactKeys={contactKeys}
              dataKeys={dataKeys}
              originalContact={formSchema.contactForm}
              complementToken={formSchema.complementToken}
              error={error && (
                <PlrNotice tone="error" testId="preloan-fill-form-error">
                  {errorExternalInformation ? EXTERNAL_INFO_ERROR : GENERIC_ERROR}
                </PlrNotice>
              )}
            />
          </PlrShell>
        </Form>
      )}
    </Formik>
  );
}
